import { Capacitor } from '@capacitor/core';
import { StatusBar } from '@capacitor/status-bar';
import { fireAndForget } from '../utils/fireAndForget';
import { applyNativeChrome } from '../utils/nativeChrome';
import { currentResolvedTheme } from '../utils/theme';

export const isIOS = Capacitor.getPlatform() === 'ios';

interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Safe Area Service for handling status bar and safe area configuration
 * Ensures proper display on devices with notches, rounded corners, etc.
 * Includes fallbacks for older Android devices that don't support CSS env() or var()
 */
class SafeAreaService {
  private isInitialized: boolean;
  private safeAreaValues: SafeAreaInsets;
  private isOldAndroid: boolean;

  constructor() {
    this.isInitialized = false;
    this.safeAreaValues = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };
    this.isOldAndroid = false;
  }

  /**
   * Initialize safe area handling and status bar configuration
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    console.log('Initializing safe area service...');

    try {
      // Detect if we're on an older Android device
      this.detectOldAndroid();

      if (Capacitor.isNativePlatform()) {
        await this.configureStatusBar();
      }

      // Apply safe area values using JavaScript for older devices
      this.applySafeAreaValues();
      this.addSafeAreaDebugInfo();
      this.isInitialized = true;
      console.log('Safe area service initialized successfully');
    } catch (error) {
      console.error('Error initializing safe area service:', error);
    }
  }

  /**
   * Detect if we're running on an older Android device
   */
  detectOldAndroid(): void {
    if (typeof window === 'undefined') return;

    const userAgent = navigator.userAgent;
    const isAndroid = /Android/i.test(userAgent);

    if (isAndroid) {
      // Extract Android version
      const androidVersionMatch = userAgent.match(/Android (\d+)/);
      const androidVersion = androidVersionMatch ? parseInt(androidVersionMatch[1]) : 0;

      // Consider Android < 7 as "old" (doesn't support CSS env() well)
      this.isOldAndroid = androidVersion > 0 && androidVersion < 7;

      console.log(
        `Android version detected: ${androidVersion}, isOldAndroid: ${this.isOldAndroid}`,
      );
    }
  }

  /**
   * Apply safe area values using JavaScript for older devices
   */
  applySafeAreaValues(): void {
    if (typeof window === 'undefined') return;

    // Get safe area values
    this.calculateSafeAreaValues();

    // Apply values to CSS custom properties
    this.setCSSVariables();

    // Apply values directly to elements for older Android
    if (this.isOldAndroid) {
      this.applyDirectStyles();
    }
  }

  /**
   * Calculate safe area values based on device capabilities
   */
  calculateSafeAreaValues(): void {
    if (typeof window === 'undefined') return;

    // On non-native platforms (desktop browsers), safe areas should be 0
    if (!Capacitor.isNativePlatform()) {
      this.safeAreaValues = { top: 0, right: 0, bottom: 0, left: 0 };
      return;
    }

    // Default values for native devices
    let top = 24; // Default status bar height
    let bottom = 0;
    let left = 0;
    let right = 0;

    // Try to get values from CSS env() if supported
    if (this.supportsCSSEnv()) {
      const computedStyle = getComputedStyle(document.documentElement);
      const envTop = computedStyle.getPropertyValue('env(safe-area-inset-top)');
      const envBottom = computedStyle.getPropertyValue('env(safe-area-inset-bottom)');
      const envLeft = computedStyle.getPropertyValue('env(safe-area-inset-left)');
      const envRight = computedStyle.getPropertyValue('env(safe-area-inset-right)');

      // Use parsed value if valid, including 0 (don't use || which treats 0 as falsy)
      const parsedTop = parseFloat(envTop);
      const parsedBottom = parseFloat(envBottom);
      const parsedLeft = parseFloat(envLeft);
      const parsedRight = parseFloat(envRight);

      if (envTop && envTop !== 'env(safe-area-inset-top)' && !isNaN(parsedTop)) {
        top = parsedTop;
      }
      if (envBottom && envBottom !== 'env(safe-area-inset-bottom)' && !isNaN(parsedBottom)) {
        bottom = parsedBottom;
      }
      if (envLeft && envLeft !== 'env(safe-area-inset-left)' && !isNaN(parsedLeft)) {
        left = parsedLeft;
      }
      if (envRight && envRight !== 'env(safe-area-inset-right)' && !isNaN(parsedRight)) {
        right = parsedRight;
      }
    } else {
      // Fallback for older devices - estimate based on screen size and orientation
      const screenHeight = window.screen.height;

      // Estimate status bar height based on screen density
      const density = window.devicePixelRatio || 1;
      top = Math.max(20, Math.round(24 * density));

      // Estimate bottom safe area for devices with home indicators
      if (screenHeight > 800) {
        // Likely has home indicator
        bottom = Math.round(20 * density);
      }
    }

    this.safeAreaValues = { top, right, bottom, left };
    console.log('Calculated safe area values:', this.safeAreaValues);
  }

  /**
   * Check if CSS env() is supported
   */
  supportsCSSEnv(): boolean {
    if (typeof window === 'undefined') return false;

    try {
      // Test if CSS env() is supported
      const testEl = document.createElement('div');
      testEl.style.paddingTop = 'env(safe-area-inset-top, 0px)';
      return testEl.style.paddingTop !== '';
    } catch (_e) {
      return false;
    }
  }

  /**
   * Set CSS custom properties
   */
  setCSSVariables(): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.style.setProperty('--safe-area-inset-top', `${this.safeAreaValues.top}px`);
    root.style.setProperty('--safe-area-inset-right', `${this.safeAreaValues.right}px`);
    root.style.setProperty('--safe-area-inset-bottom', `${this.safeAreaValues.bottom}px`);
    root.style.setProperty('--safe-area-inset-left', `${this.safeAreaValues.left}px`);
  }

  /**
   * Apply direct styles for older Android devices
   */
  applyDirectStyles(): void {
    if (typeof document === 'undefined') return;

    // Add a class to body to identify old Android
    document.body.classList.add('old-android-safe-area');

    // Create a style element with direct CSS for older devices
    const styleId = 'old-android-safe-area-styles';
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    const { top, right, bottom, left } = this.safeAreaValues;

    styleEl.textContent = `
      .old-android-safe-area .safe-area-top {
        padding-top: ${top}px !important;
      }
      .old-android-safe-area .safe-area-bottom {
        padding-bottom: ${bottom}px !important;
      }
      .old-android-safe-area .safe-area-left {
        padding-left: ${left}px !important;
      }
      .old-android-safe-area .safe-area-right {
        padding-right: ${right}px !important;
      }
      .old-android-safe-area .safe-area-horizontal {
        padding-left: ${left}px !important;
        padding-right: ${right}px !important;
      }
      .old-android-safe-area .fixed-bottom-safe {
        bottom: ${bottom}px !important;
      }
    `;
  }

  /**
   * Configure the status bar appearance and behavior: the native chrome of
   * the theme the boot script chose (white bars and dark icons in light, as
   * before dark mode; utils/nativeChrome.ts). The theme store re-applies it
   * on every theme change.
   */
  async configureStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      console.log('Configuring status bar...');
      setTimeout(
        fireAndForget(async () => {
          await applyNativeChrome(currentResolvedTheme());
        }),
        1000,
      );
      console.log('Status bar configured successfully');
    } catch (error) {
      console.error('Error configuring status bar:', error);
    }
  }

  /**
   * Hide status bar (useful for fullscreen modes)
   */
  async hideStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await StatusBar.hide();
      console.log('Status bar hidden');
    } catch (error) {
      console.error('Error hiding status bar:', error);
    }
  }

  /**
   * Show status bar
   */
  async showStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await StatusBar.show();
      console.log('Status bar shown');
    } catch (error) {
      console.error('Error showing status bar:', error);
    }
  }

  /**
   * Get current status bar info
   */
  async getStatusBarInfo(): Promise<{ visible: boolean; style: string; color?: string } | null> {
    if (!Capacitor.isNativePlatform()) {
      return { visible: true, style: 'light', color: '#ffffff' };
    }

    try {
      const info = await StatusBar.getInfo();
      console.log('Status bar info:', info);
      return {
        visible: info.visible,
        style: info.style.toString(),
        color: info.color,
      };
    } catch (error) {
      console.error('Error getting status bar info:', error);
      return null;
    }
  }

  /**
   * Add debug information about safe areas to the console
   * Helpful for testing on different devices
   */
  addSafeAreaDebugInfo(): void {
    if (typeof window === 'undefined') return;

    // Log safe area values
    const logSafeAreas = (): void => {
      console.log('Current safe area insets:', this.safeAreaValues);
      console.log('Is old Android:', this.isOldAndroid);

      // Add visual indicators in debug mode
      if (process.env.NODE_ENV === 'development') {
        this.addVisualDebugIndicators(this.safeAreaValues);
      }
    };

    // Log immediately and on resize
    setTimeout(logSafeAreas, 1000);
    window.addEventListener('resize', logSafeAreas);
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.calculateSafeAreaValues();
        this.setCSSVariables();
        if (this.isOldAndroid) {
          this.applyDirectStyles();
        }
        logSafeAreas();
      }, 500);
    });
  }

  /**
   * Add visual debug indicators for safe areas (development only, native platforms only)
   * @param {Object} safeAreas - Safe area inset values
   */
  addVisualDebugIndicators(safeAreas: SafeAreaInsets): void {
    // Remove existing indicators
    document.querySelectorAll('.safe-area-debug').forEach((el) => el.remove());

    // Only show debug indicators on native platforms (not desktop browsers)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const createIndicator = (position: string, size: number, color: string): void => {
      const indicator = document.createElement('div');
      indicator.className = 'safe-area-debug';
      indicator.style.cssText = `
        position: fixed;
        background-color: ${color};
        opacity: 0.3;
        z-index: 9999;
        pointer-events: none;
        ${position === 'top' ? `top: 0; left: 0; right: 0; height: ${size}px;` : ''}
        ${position === 'bottom' ? `bottom: 0; left: 0; right: 0; height: ${size}px;` : ''}
        ${position === 'left' ? `left: 0; top: 0; bottom: 0; width: ${size}px;` : ''}
        ${position === 'right' ? `right: 0; top: 0; bottom: 0; width: ${size}px;` : ''}
      `;
      document.body.appendChild(indicator);
    };

    // Only show indicators if there are actual safe area insets
    if (safeAreas.top > 0) {
      createIndicator('top', safeAreas.top, 'red');
    }
    if (safeAreas.bottom > 0) {
      createIndicator('bottom', safeAreas.bottom, 'blue');
    }
    if (safeAreas.left > 0) {
      createIndicator('left', safeAreas.left, 'green');
    }
    if (safeAreas.right > 0) {
      createIndicator('right', safeAreas.right, 'orange');
    }
  }

  /**
   * Handle orientation changes
   */
  handleOrientationChange(): void {
    if (!Capacitor.isNativePlatform()) return;

    // Re-initialize safe areas after orientation change
    setTimeout(() => {
      this.calculateSafeAreaValues();
      this.setCSSVariables();
      if (this.isOldAndroid) {
        this.applyDirectStyles();
      }
      this.addSafeAreaDebugInfo();
    }, 300);
  }

  /**
   * Get current safe area values
   */
  getSafeAreaValues(): SafeAreaInsets {
    return { ...this.safeAreaValues };
  }
}

// Export singleton instance
export const safeAreaService = new SafeAreaService();
export default safeAreaService;
